import React, { useState, useMemo, useEffect } from 'react';
import { 
  TextInput, 
  Select, 
  Stack,
  Button,
  Group,
  Paper,
  Text,
  Box,
  Textarea,
  Checkbox,
  Alert,
  Tooltip,
  ActionIcon
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import moment from 'moment-timezone';
import './AppointmentForm.css';
import { useSalon } from '../../../../../context/SalonContext';
import { useCalendar } from '../../../../../context/CalendarContext';
import { IconInfoCircle } from '@tabler/icons-react';
import ClientLookup from '../../../../common/ClientLookup';

function AppointmentForm({ initialData, onSubmit, onCancel, isEdit }) {
  const { employees, services } = useSalon();
  const { staffServices, selectedDate } = useCalendar();
  // Add new state to track available options
  const [availableServices, setAvailableServices] = useState(services);
  const [availableStaff, setAvailableStaff] = useState(employees);

  // Generate time slots from 7:00 AM to 9:00 PM in 15-minute intervals
  const timeSlots = useMemo(() => {
    const slots = [];
    const startTime = moment().set({ hour: 0, minute: 0 });
    const endTime = moment().set({ hour: 23, minute: 55 });

    while (startTime <= endTime) {
      slots.push({
        value: startTime.format('HH:mm'),
        label: startTime.format('h:mm A')
      });
      startTime.add(5, 'minutes');
    }
    return slots;
  }, []);

  const mapInitialDataToFormData = (initialData) => {
    return {
      salonServiceId: initialData?.salonServiceId || '',
      employeeId: initialData?.employeeId || '',
      startTime: initialData?.startTime || '',
      endTime: initialData?.endTime || '',
      date: initialData?.selectedDate || selectedDate,
      duration: {
        hours: Math.floor(initialData?.duration / 60).toString() || '0',
        minutes: (initialData?.duration % 60).toString() || '30'
      },
      salonNotes: initialData?.salonNotes || '',
      clientUuid: initialData?.clientUuid || ''
    };
  };

  const [formData, setFormData] = useState(mapInitialDataToFormData(initialData));

  useEffect(() => {
    setFormData(mapInitialDataToFormData(initialData));
  }, [initialData]);

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    employeeId: '',
    salonServiceId: '',
    clientUuid: ''
  });

  // Create arrays for hours and minutes options
  const hourOptions = Array.from({ length: 6 }, (_, i) => ({
    value: i.toString(),
    label: `${i}hr`
  }));

  const minuteOptions = Array.from({ length: 12 }, (_, i) => ({
    value: (i * 5).toString(),
    label: `${i * 5}min`
  }));

  // Modify updateDurationAndEndTime to return the new values instead of setting state
  const calculateDurationAndEndTime = (staffId, serviceId, startTime) => {
    if (!staffId || !serviceId) return null;

    const selectedService = services.find(s => s.id === serviceId);
    if (!selectedService) return null;

    const staffService = selectedService.servicing_staff.find(s => s.staff_id === staffId);
    if (!staffService) return null;

    const hours = Math.floor(staffService.duration_mins / 60);
    const minutes = staffService.duration_mins % 60;
    
    const newDuration = {
      hours: hours.toString(),
      minutes: minutes.toString()
    };

    // Calculate end time if start time exists
    const newEndTime = startTime ? calculateEndTime(
      startTime,
      serviceId,
      newDuration
    ) : '';

    return {
      duration: newDuration,
      endTime: newEndTime
    };
  };

  const handleStartTimeChange = (value) => {
    setFormData(currentFormData => {
      const newFormData = {
        ...currentFormData,
        startTime: value
      };

      // If we have both service and staff selected, calculate new duration and end time
      if (currentFormData.salonServiceId && currentFormData.employeeId) {
        const calculated = calculateDurationAndEndTime(
          currentFormData.employeeId, 
          currentFormData.salonServiceId,
          value
        );
        
        if (calculated) {
          newFormData.duration = calculated.duration;
          newFormData.endTime = calculated.endTime;
        }
      }

      return newFormData;
    });
  };

  // Update the original updateDurationAndEndTime to use the new calculation function
  const updateDurationAndEndTime = (staffId, serviceId) => {
    setFormData(prev => {
      const calculated = calculateDurationAndEndTime(staffId, serviceId, prev.startTime);
      if (calculated) {
        return {
          ...prev,
          duration: calculated.duration,
          endTime: calculated.endTime
        };
      }
      return prev;
    });
  };

  // Handle staff selection
  const handleStaffSelect = (staffId) => {
    setFormData(prev => ({ 
      ...formData, 
      employeeId: staffId,
    }));

    if (staffId) {
      // Filter staffServices to get only services this staff member can provide
      const staffServiceIds = staffServices
        .filter(service => service.staffId === staffId)
        .map(service => service.salonServiceId);

      // Filter salonServices to only show services this staff member can provide
      const filteredServices = services.filter(service => 
        staffServiceIds.includes(service.id)
      );

      setAvailableServices(filteredServices);
      setErrors({ ...errors, employeeId: '' });

      // Update duration if a service is already selected
      if (formData.salonServiceId) {
        updateDurationAndEndTime(staffId, formData.salonServiceId);
      }
    } else {
      // Reset to all services if no staff selected
      setAvailableServices(services);
    }
  };

  // Handle service selection
  const handleServiceSelect = (serviceId) => {
    setFormData(prev => ({ 
      ...formData, 
      salonServiceId: serviceId,
    }));

    if (serviceId) {
      // Find the selected service
      const selectedService = services.find(s => s.id === serviceId);
      
      if (selectedService) {
        // Get all staff IDs that can provide this service
        const availableStaffIds = selectedService.servicing_staff.map(staff => staff.staff_id);
        
        // Filter employees to only show those who can provide this service
        const filteredStaff = employees.filter(emp => 
          availableStaffIds.includes(emp.id)
        );

        setAvailableStaff(filteredStaff);
        setErrors({ ...errors, salonServiceId: '' });

        // Update duration if a staff member is already selected
        if (formData.employeeId) {
          updateDurationAndEndTime(formData.employeeId, serviceId);
        }
      }
    } else {
      // Reset to all staff if no service selected
      setAvailableStaff(employees);
    }
  };

  // Update calculateEndTime to use staff+service specific duration
  const calculateEndTime = (startTime, salonServiceId, customDuration = null) => {
    if (!startTime) return '';
    
    let duration;
    if (customDuration) {
      duration = (parseInt(customDuration.hours) * 60) + parseInt(customDuration.minutes);
    } else {
      const service = services.find(s => s.id === salonServiceId);
      if (!service) return '';
      
      // Find staff-specific duration
      const staffService = service.servicing_staff.find(s => s.staff_id === formData.employeeId);
      duration = staffService ? staffService.duration_mins : null;
    }

    const endTime = moment(startTime, 'HH:mm')
      .add(duration, 'minutes')
      .format('HH:mm');
    
    return endTime;
  };

  const handleDurationChange = (type, value) => {
    const newDuration = {
      ...formData.duration,
      [type]: value
    };
    
    setFormData(prev => ({
      ...prev,
      duration: newDuration
    }));

    if (formData.startTime) {
      const endTime = calculateEndTime(formData.startTime, formData.salonServiceId, newDuration);
      setFormData(prev => ({
        ...prev,
        endTime: endTime
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      employeeId: !formData.employeeId ? 'Artist is required' : '',
      salonServiceId: !formData.salonServiceId ? 'Service is required' : '',
      clientUuid: !formData.clientUuid ? 'Client is required' : ''
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Create Date objects for start and end times using the selected date
    const startDateTime = moment.tz(formData.date, 'America/New_York')
      .hour(moment(formData.startTime, 'HH:mm').hour())
      .minute(moment(formData.startTime, 'HH:mm').minute())
      .toDate();

    const endDateTime = moment.tz(formData.date, 'America/New_York')
      .hour(moment(formData.endTime, 'HH:mm').hour())
      .minute(moment(formData.endTime, 'HH:mm').minute())
      .toDate();

    const editSpecificFields = isEdit ? {} : {
      booked_services: [{
        staff_service_id: staffServices.find(
          service => service.staffId === formData.employeeId && 
                    service.salonServiceId === formData.salonServiceId
        )?.id
      }],
      source: "Manually input"
    };

    const appointmentData = {
      start_dt: startDateTime,
      end_dt: endDateTime,
      salon_notes: formData.salonNotes,
      client_uuid: formData.clientUuid,
      ...editSpecificFields
    };

    onSubmit(appointmentData);
  };

  return (
    <Paper p="sm" pt="0" className="appointment-form">
      <Stack spacing="xs">
        <form onSubmit={handleSubmit}>
          <Stack spacing="sm">
            <DateInput
              label="Date"
              required
              value={formData.date}
              onChange={(value) => setFormData({ ...formData, date: value })}
              minDate={new Date()}
              clearable
              valueFormat="MMMM DD, YYYY"
              placeholder="Select date"
              firstDayOfWeek={0}
              styles={(theme) => ({
                input: {
                  '&:focus': {
                    borderColor: theme.colors.violet[5]
                  }
                }
              })}
            />
            <ClientLookup
              value={formData.clientUuid}
              onChange={(value) => {
                setFormData({ ...formData, clientUuid: value });
                if (value) {
                  setErrors({ ...errors, clientUuid: '' });
                }
              }}
              error={errors.clientUuid}
            />


            <Select
              label="Artist"
              required
              placeholder="Select Artist"
              data={availableStaff.map(emp => ({
                value: emp.id,
                label: `${emp.first_name} ${emp.last_name}`
              }))}
              value={formData.employeeId}
              onChange={handleStaffSelect}
              error={errors.employeeId}
              clearable
              disabled={isEdit}
              styles={(theme) => ({
                input: {
                  '&:focus': {
                    borderColor: theme.colors.violet[5]
                  }
                }
              })}
            />

            <Select
              label="Service"
              required
              placeholder="Select Service"
              data={(availableServices ? availableServices.map(service => ({
                value: service.id,
                label: service.name
              })) : [])}
              value={formData.salonServiceId}
              onChange={handleServiceSelect}
              error={errors.salonServiceId}
              disabled={isEdit}
              clearable
              styles={(theme) => ({
                input: {
                  '&:focus': {
                    borderColor: theme.colors.violet[5]
                  }
                }
              })}
            />

            <Textarea
              label="Salon Notes"
              placeholder="Add any notes about the appointment (500 characters max)"
              value={formData.salonNotes}
              onChange={(e) => {
                const value = e.target.value.slice(0, 500); // Limit to 500 chars
                setFormData(prev => ({ ...prev, salonNotes: value }));
              }}
              maxLength={500}
              autosize
              minRows={2}
              maxRows={4}
              styles={(theme) => ({
                input: {
                  '&:focus': {
                    borderColor: theme.colors.violet[5]
                  }
                }
              })}
            />

            <Box>
              <Group align="end" spacing="xs">
                <Select
                  placeholder="Start Time"
                  label="Schedule"
                  data={timeSlots}
                  value={formData.startTime}
                  onChange={handleStartTimeChange}
                  searchable
                  maxDropdownHeight={200}
                  required
                />
                <Text pb="4px">to</Text>
                <Select
                  placeholder="End Time"
                  data={timeSlots}
                  value={formData.endTime}
                  searchable
                  maxDropdownHeight={200}
                  disabled
                />
              </Group>
            </Box>

            <Box>
              <Group gap="sm" align="end">
                <Select
                  placeholder="Hours"
                  label="Duration"
                  data={hourOptions}
                  value={formData.duration.hours}
                  onChange={(value) => handleDurationChange('hours', value)}
                  style={{ width: '100px' }}
                />
                <Select
                  placeholder="Minutes"
                  data={minuteOptions}
                  value={formData.duration.minutes}
                  onChange={(value) => handleDurationChange('minutes', value)}
                  style={{ width: '100px' }}
                />
              </Group>
            </Box>

            <Button 
              type="submit"
              fullWidth 
              size="md"
              color="violet"
              mt="xl"
              mb="xs"
            >
              Save
            </Button>

            {isEdit && (
              <Button 
                fullWidth 
                variant="subtle"
                color="red"
                size="md"
                onClick={() => onCancel(initialData)}
              >
                Cancel Appointment
              </Button>
            )}
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}

export default AppointmentForm; 